// Tiny shared loader for the Google Maps JS API using the modern
// `loading=async` bootstrap + `importLibrary` pattern. This is the
// recommended loading approach as of 2024+ and is required to use
// `AdvancedMarkerElement` (the replacement for the deprecated
// `google.maps.Marker`).
//
// Usage:
//   const { google } = await loadGoogleMaps();
//   const { Map } = await google.maps.importLibrary('maps');
//   const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let loadPromise = null;

export function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('window unavailable'));
  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    return Promise.resolve({ google: window.google });
  }
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    if (!KEY) {
      reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set'));
      return;
    }
    const existing = document.getElementById('google-maps-js');
    const finish = () => {
      // Give the bootstrap loader a tick to attach `importLibrary`.
      const tick = () => {
        if (window.google && window.google.maps && window.google.maps.importLibrary) {
          resolve({ google: window.google });
        } else {
          setTimeout(tick, 20);
        }
      };
      tick();
    };
    if (existing) {
      existing.addEventListener('load', finish);
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = 'google-maps-js';
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&v=weekly&loading=async&libraries=marker`;
    s.onload = finish;
    s.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/** Geocode an address to { lat, lng }.
 *
 * Strategy:
 *   1. Prefer `google.maps.Geocoder` (loaded via the JS API). This uses
 *      the page's referrer for auth, which matches the same restrictions
 *      already in place for the Maps JS API key — much more likely to
 *      succeed than the REST endpoint that often needs an additional
 *      "Geocoding API" enablement + different key restrictions.
 *   2. Fall back to the REST endpoint if the JS API failed/unavailable.
 *
 * Throws on hard failures (auth, network, etc.) so callers can surface
 * errors to the user — silent swallowing was the root cause of the
 * "map not set despite entering address" bug. Returns null only for
 * legitimate ZERO_RESULTS responses.
 */
export async function geocodeAddress(address) {
  const cleaned = (address || '').trim();
  if (!cleaned) return null;

  // ── Path 1: JS API Geocoder (preferred) ─────────────────────
  try {
    const { google } = await loadGoogleMaps();
    if (google && google.maps) {
      const geocoder = new google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: cleaned }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else if (status === 'ZERO_RESULTS') {
            resolve(null);
          } else {
            reject(new Error(`Geocoder status: ${status}`));
          }
        });
      });
      return result;
    }
  } catch (err) {
    console.warn('[geocode] JS Geocoder failed, falling back to REST:', err);
  }

  // ── Path 2: REST endpoint (fallback) ────────────────────────
  if (!KEY) throw new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set');
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cleaned)}&key=${KEY}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Geocoding HTTP ${res.status}`);
  const data = await res.json();
  if (data.status === 'OK' && data.results && data.results[0]) {
    const loc = data.results[0].geometry.location;
    return { lat: Number(loc.lat), lng: Number(loc.lng) };
  }
  if (data.status === 'ZERO_RESULTS') return null;
  throw new Error(`Geocoding failed: ${data.status}${data.error_message ? ` — ${data.error_message}` : ''}`);
}
